import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { fileUrl } from '@/lib/api';

/**
 * Fullscreen image lightbox with keyboard, swipe, and reduced-motion support.
 * Props:
 *   photos: Array<{ id, image, caption? }>
 *   initialIndex: number — which photo to open at
 *   isOpen: boolean
 *   onClose: () => void
 *   totalCount: number | null — API-reported total across all pages
 *   hasMore: boolean — more pages are available beyond `photos`
 *   onRequestMore: (() => void) | null — trigger a next-page load
 *   loadingMore: boolean — a next-page request is currently in flight
 *
 * Backward compatibility: with totalCount=null, hasMore=false and
 * onRequestMore=null, `displayTotal` equals `total`, `pendingFrom` never
 * leaves null, neither arrow button ever disables, and next/prev wrap as
 * they did before this order.
 */
export default function Lightbox({
  photos = [],
  initialIndex = 0,
  isOpen,
  onClose,
  totalCount = null,
  hasMore = false,
  onRequestMore = null,
  loadingMore = false,
}) {
  const [index, setIndex] = useState(initialIndex);
  // The boundary the user asked to cross — a number, or null when no cross
  // is pending. Never a boolean.
  const [pendingFrom, setPendingFrom] = useState(null);
  const touchStartX = useRef(null);

  const total = photos.length;
  // Validated display total. Prefer the caller's authoritative total when
  // it is a plausible integer at least as large as the loaded slice;
  // otherwise fall back to the loaded count. Callers that omit totalCount
  // get exactly the old behaviour.
  const displayTotal =
    Number.isInteger(totalCount) && totalCount >= total
      ? totalCount
      : total;
  const photo = total > 0 ? photos[((index % total) + total) % total] : null;

  // Reset index whenever it opens; also clear any pending boundary.
  useEffect(() => {
    if (isOpen) { setIndex(initialIndex); setPendingFrom(null); }
  }, [isOpen, initialIndex]);

  const next = useCallback(() => {
    // At the end of what is loaded, ask the caller for more instead of
    // wrapping. Record the boundary and hold position.
    if (index >= total - 1 && hasMore && onRequestMore) {
      setPendingFrom(total);
      onRequestMore();
      return;
    }
    setIndex((i) => (i + 1) % total);
  }, [index, total, hasMore, onRequestMore]);

  const prev = useCallback(() => {
    // Backward navigation cancels a pending advance. While more pages
    // remain, do NOT wrap from the first photo to the last loaded one
    // — that is not the previous photo.
    setPendingFrom(null);
    setIndex((i) => (hasMore && i <= 0 ? 0 : (i - 1 + total) % total));
  }, [hasMore, total]);

  // Advance ONLY when the loaded array actually grew past the recorded
  // boundary. `index` is deliberately NOT a dependency so that navigating
  // cannot re-trigger this effect.
  useEffect(() => {
    if (pendingFrom === null) return;
    if (total > pendingFrom) {
      setIndex(pendingFrom);
      setPendingFrom(null);
    }
  }, [pendingFrom, total]);

  // Body scroll lock + keyboard handlers
  useEffect(() => {
    if (!isOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose, next, prev]);

  if (!isOpen || !photo) return null;

  // Backdrop click closes
  const onBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const onTouchStart = (e) => {
    touchStartX.current = e.touches?.[0]?.clientX ?? null;
  };
  const onTouchEnd = (e) => {
    const startX = touchStartX.current;
    touchStartX.current = null;
    if (startX == null) return;
    const endX = e.changedTouches?.[0]?.clientX ?? startX;
    const dx = endX - startX;
    if (Math.abs(dx) > 40) {
      if (dx < 0) next();
      else prev();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-card-dark/90 motion-safe:transition-opacity motion-safe:duration-200"
      onClick={onBackdropClick}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      role="dialog"
      aria-modal="true"
      aria-label="Photo gallery"
      data-testid="lightbox"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close gallery"
        className="absolute top-4 right-4 md:top-6 md:right-6 z-10 w-11 h-11 md:w-12 md:h-12 rounded-full border-2 border-brand-orange text-brand-orange bg-card-dark/40 hover:bg-brand-orange hover:text-white motion-safe:transition-all motion-safe:duration-200 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 focus-visible:ring-offset-card-dark"
        data-testid="lightbox-close"
      >
        <X size={20} />
      </button>

      {total > 1 && (
        <button
          type="button"
          onClick={prev}
          disabled={hasMore && index <= 0}
          aria-label="Previous photo"
          className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 md:w-12 md:h-12 rounded-full border-2 border-brand-orange text-brand-orange bg-card-dark/40 hover:bg-brand-orange hover:text-white motion-safe:transition-all motion-safe:duration-200 motion-safe:hover:scale-105 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 focus-visible:ring-offset-card-dark disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="lightbox-prev"
        >
          <ChevronLeft size={22} />
        </button>
      )}

      {total > 1 && (
        <button
          type="button"
          onClick={next}
          disabled={pendingFrom !== null && loadingMore}
          aria-label="Next photo"
          className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 md:w-12 md:h-12 rounded-full border-2 border-brand-orange text-brand-orange bg-card-dark/40 hover:bg-brand-orange hover:text-white motion-safe:transition-all motion-safe:duration-200 motion-safe:hover:scale-105 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 focus-visible:ring-offset-card-dark disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="lightbox-next"
        >
          <ChevronRight size={22} />
        </button>
      )}

      <div className="relative max-w-[92vw] max-h-[90vh] flex flex-col items-center justify-center px-4">
        <img
          src={fileUrl(photo.image) || photo.image}
          alt={photo.caption || `Photo ${index + 1} of ${displayTotal}`}
          className="max-w-full max-h-[80vh] object-contain rounded-sm motion-safe:transition-opacity motion-safe:duration-200"
          data-testid="lightbox-image"
        />
        {photo.caption && (
          <p className="mt-4 font-body text-[13px] text-white/80 text-center max-w-[640px]" data-testid="lightbox-caption">
            {photo.caption}
          </p>
        )}
        {total > 1 && (
          <p className="mt-3 font-body text-[11px] uppercase tracking-wider2 text-white/60" data-testid="lightbox-counter">
            {index + 1} / {displayTotal}
          </p>
        )}
      </div>
    </div>
  );
}
